/* eslint-disable @typescript-eslint/no-magic-numbers */
import React from 'react';
import omit from 'lodash/omit';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { Box } from '@cp/ds/src/components/box';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { Container } from '@cp/ds/src/components/container';
import { ModalView } from '@cp/ds/src/components/modalView';
import { SwipeableCarouselWithAssessment } from '@cp/ds/src/components/swipeableCarouselWithAssessment';
import { useTheme } from '@cp/ds/src/hooks';
import { CatalogItemViewContext } from '../../model';
import { CatalogItemModalViewProps, CatalogItemModalViewType } from './types';

export * from './types';

const HORIZONTAL_SPACING_DESKTOP = 5;

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const CatalogItemModalView = <ItemData extends unknown>(props: CatalogItemModalViewProps<ItemData>) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isVacancy = props.catalogItemType === CatalogItemType.Vacancy;

  const views = React.useContext(CatalogItemViewContext);
  const View = props.catalogItemType ? views[props.catalogItemType] : null;

  const ViewContainer = isMobile && isVacancy ? Container : Box;
  const FooterContainer = isMobile ? Container : Box;

  if (!View) {
    return null;
  }

  const renderContent = () => {
    if (props.type === CatalogItemModalViewType.Single) {
      return (
        <ViewContainer>
          <View {...props.viewProps} />
        </ViewContainer>
      );
    } else if (props.type === CatalogItemModalViewType.List) {
      return (
        <SwipeableCarouselWithAssessment
          itemContainerProps={{
            sx: {
              borderTopLeftRadius: `${theme.borderRadius.large}px`,
              borderTopRightRadius: `${theme.borderRadius.large}px`,
              overflow: 'hidden',
            },
          }}
          {...props.viewProps}
        />
      );
    } else {
      throw new Error(`[CatalogItemModalView]: type ${(props as CatalogItemModalViewProps<ItemData>).type} is not supported!`);
    }
  };

  const modalViewProps = omit(props, ['type', 'viewProps']);

  return (
    <ModalView
      config={{
        desktop: 'drawerRight',
        mobile: 'drawerBottom',
      }}
      containerSx={{ width: { xs: 1, md: 780 } }}
      keepMounted={false}
      scrollableContainerSx={{
        pt: { xs: isVacancy ? 1.5 : 0, md: HORIZONTAL_SPACING_DESKTOP },
        pb: 1.5,
        px: { xs: 0, md: HORIZONTAL_SPACING_DESKTOP },
        ...(props.type === CatalogItemModalViewType.List && props.viewProps.showInstruction
          ? { display: 'flex', overflowY: 'hidden' }
          : undefined),
      }}
      showCloseButton={isVacancy || !isMobile}
      {...modalViewProps}
      buttons={
        modalViewProps.buttons ? (
          <FooterContainer sx={isMobile ? undefined : { px: HORIZONTAL_SPACING_DESKTOP }}>{modalViewProps.buttons}</FooterContainer>
        ) : undefined
      }
    >
      {renderContent()}
    </ModalView>
  );
};
